import { useMemo } from 'react';
import { EventPlaceType, PointOnMapType, useEventPlacesQuery } from '@graphql/generated';
import { useEventInfo } from '../eventInfo/useEventInfo';
import { EventVenue } from './EventPlace.types';
import { isValidLatLngLocation } from '@shared/utils/locationValidation';

/**
 **
 * Returns venue about the currently loaded event.
 * @param {boolean} [isAdmin=true] - A flag indicating whether the current user is an admin.
 * @param {boolean} [skip=false] - If true, skips the GraphQL query execution.
 */
export function useEventPlaces(isAdmin: boolean = true, skip: boolean = false) {
  const { eventHandle } = useEventInfo();

  const { data, ...rest } = useEventPlacesQuery({
    variables: {
      name: eventHandle!,
      payload: {
        eventName: eventHandle!,
        pageSlug: 'accommodations',
      },
      isAdmin,
    },
    batchMode: 'fast',
    skip: !eventHandle || skip,
  });

  // We have to use existing venue id b/c event place don't have id for existing venue.
  const findTravelMap = data?.pageContainerBySlug?.components?.find(component => component.__typename === 'TravelMapComponent');
  const travelMapComponent = findTravelMap?.__typename === 'TravelMapComponent' ? findTravelMap : undefined;
  const pointOnMapId = travelMapComponent?.pointsOnMap?.find(pointsOnMap => pointsOnMap?.type === PointOnMapType.venue)?.id;

  const eventPlaces = data?.eventByName?.places ?? [];
  const locationAnchor = data?.eventByName?.bestLocationAnchor;

  const bestLocationAnchor = locationAnchor
    ? {
        latitude: parseFloat(locationAnchor.latitude),
        longitude: parseFloat(locationAnchor.longitude),
        googlePlaceID: locationAnchor.googlePlaceID,
        friendlyName: locationAnchor.friendlyName,
      }
    : null;

  const venue = eventPlaces.find(place => place.type === EventPlaceType.venue);
  const displayName = venue?.name ? `${venue.name}${venue?.displayAddress ? ', ' + venue?.displayAddress : ''}` : null;
  const venueInfo: EventVenue = useMemo(
    () => ({
      id: venue?.id,
      googlePlaceId: venue?.googlePlaceId,
      name: venue?.name,
      displayName,
      address: venue?.displayAddress,
      latitude: venue?.latitude,
      longitude: venue?.longitude,
      city: venue?.city,
      stateProvince: venue?.stateProvince,
      country: venue?.country,
      countryCode: venue?.countryCode,
      pointOnMapId,
      hasVenue: Boolean(displayName),
    }),
    // Other fields are invariant relative to the ID.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [venue?.id, venue?.googlePlaceId],
  );

  const origin =
    venue && isValidLatLngLocation(venue)
      ? { latitude: venue.latitude as number, longitude: venue.longitude as number, googlePlaceID: venue?.googlePlaceId, friendlyName: venue?.name }
      : bestLocationAnchor;

  return { eventPlaces, venueInfo, origin, ...rest };
}
